import React from 'react';
import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from 'framer-motion';
import { Home, ArrowRight, Search, MessageSquare } from 'lucide-react';
import SEO from '@/components/SEO';

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  // Simple fade-in animation variant
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <>
      <SEO 
        title="Page non trouvée (404)"
        description="La page que vous recherchez n'existe pas ou a été déplacée. Retournez à l'accueil pour découvrir nos services de développement web, mobile et SaaS."
        keywords="404, page non trouvée, erreur, orcsys, développement web, développement mobile"
        type="error"
        url="https://innov.orcsys.fr/404"
        image="https://innov.orcsys.fr/images/404-preview.jpg"
      />
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <motion.div
          className="container px-4 mx-auto"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="max-w-4xl mx-auto text-center">
            {/* 404 Text */}
            <div className="relative inline-block mb-8">
              <span className="text-[10rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-orcsys-500 to-orcsys-600 leading-none">
                404
              </span>
            </div>

            {/* Main message */}
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Oups ! Cette page semble avoir disparu
            </h1>
            
            <p className="text-lg text-slate-600 mb-12">
              La page que vous recherchez n'existe pas ou a été déplacée.
              <br />
              Pas d'inquiétude, voici quelques suggestions pour vous aider :
            </p>

            {/* Action buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <Link 
                to="/"
                className="group flex items-center justify-center p-6 bg-white rounded-xl shadow hover:shadow-md transition-all duration-300"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-orcsys-50 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-orcsys-100 transition-colors">
                    <Home className="w-6 h-6 text-orcsys-500" />
                  </div>
                  <h3 className="font-medium text-slate-900">Retour à l'accueil</h3>
                </div>
              </Link>

              <Link 
                to="/contact"
                className="group flex items-center justify-center p-6 bg-white rounded-xl shadow hover:shadow-md transition-all duration-300"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-orcsys-50 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-orcsys-100 transition-colors">
                    <MessageSquare className="w-6 h-6 text-orcsys-500" />
                  </div>
                  <h3 className="font-medium text-slate-900">Nous contacter</h3>
                </div>
              </Link>

              <Link 
                to="/projets"
                className="group flex items-center justify-center p-6 bg-white rounded-xl shadow hover:shadow-md transition-all duration-300"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-orcsys-50 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-orcsys-100 transition-colors">
                    <Search className="w-6 h-6 text-orcsys-500" />
                  </div>
                  <h3 className="font-medium text-slate-900">Voir nos projets</h3>
                </div>
              </Link>
            </div>

            {/* Additional help */}
            <div className="mt-16">
              <Link 
                to="/contact"
                className="inline-flex items-center text-orcsys-500 hover:text-orcsys-600 font-medium group"
              >
                Besoin d'aide pour votre projet ?
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default NotFound;
